import { getUiLang } from '@/i18n/resolveLanguage'

declare global {
  interface Date {
    // eslint-disable-next-line no-unused-vars
    getDayOfWeek(length?: number, offset?: number): string | undefined
    // eslint-disable-next-line no-unused-vars
    toTimeString(
      hasMinutes?: boolean,
      hasAmPm?: boolean,
      gmtOffset?: number,
      timezone?: string,
    ): string
    fullDate(gmtOffset?: number, hasMinutes?: boolean, hasAmPm?: boolean): string
    // eslint-disable-next-line no-unused-vars
    applyUtcOffset(gmtOffset: number): Date
    milisecondsUntilNextMinute(): number
  }
}

function uiLocale(): string {
  return getUiLang() === 'es' ? 'es-US' : 'en-US'
}

Date.prototype.applyUtcOffset = function (gmtOffset: number): Date {
  const currentUTC = DateTime()
  const offsetDiff = gmtOffset * 60 + currentUTC.getTimezoneOffset()
  const time = this != null ? this.getTime() : currentUTC.getTime()
  this.setTime(time + offsetDiff * 60 * 1000)
  return this
}

Date.prototype.getDayOfWeek = function (length = 0, offset: number): string | undefined {
  const newDate = offset ? this.applyUtcOffset(offset) : this
  const dayName = new Intl.DateTimeFormat(uiLocale(), { weekday: 'long' })
    .format(newDate)
    .toUpperCase()
  return length ? dayName.slice(0, length) : dayName
}

Date.prototype.toTimeString = function (
  hasMinutes = true,
  is12Hours: boolean | undefined = false,
  gmtOffset?: number | undefined,
  timezone = '',
): string {
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: is12Hours,
  }

  if (gmtOffset !== undefined) {
    this.applyUtcOffset(gmtOffset)
  }

  if (hasMinutes) {
    formatOptions.minute = 'numeric'
  }

  const format = new Intl.DateTimeFormat(uiLocale(), formatOptions)
  const formattedTime = format.format(this)

  if (is12Hours) {
    const amPm = this.getHours() < 12 ? 'AM' : 'PM'
    let time = formattedTime.split(' ')[0]
    time = time === '0' ? '12' : time
    if (time?.startsWith('0')) time = time?.replace('0:', '12:')
    return `${time} ${amPm} ${timezone}`.trim()
  }

  return `${formattedTime} ${timezone}`.trim()
}

Date.prototype.milisecondsUntilNextMinute = function (): number {
  return (60 - this.getSeconds()) * 1000
}

Date.prototype.fullDate = function (
  gmtOffset?: number | undefined,
  hasMinutes = true,
  is12Hours: boolean | undefined = true,
): string {
  if (gmtOffset !== undefined) {
    this.applyUtcOffset(gmtOffset)
  }

  // Layout is fixed (English: "Month D, YYYY h:mm AM/PM") regardless of locale.
  // Only the month name follows the locale, so Spanish reads "Junio 1, 2026 5:00 PM"
  // instead of "1 de junio de 2026, 5:00 p. m." that Intl would produce for es-US.
  const monthName = new Intl.DateTimeFormat(uiLocale(), { month: 'long' }).format(this)
  const month = monthName.charAt(0).toUpperCase() + monthName.slice(1)
  const day = this.getDate()
  const year = this.getFullYear()

  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: is12Hours,
  }
  if (hasMinutes) timeOpts.minute = '2-digit'

  const time = new Intl.DateTimeFormat('en-US', timeOpts).format(this)

  return `${month} ${day}, ${year} ${time}`
}
export const DateTime = (dateTimeString?: string) =>
  dateTimeString ? new Date(dateTimeString) : new Date()
